import { useNavigation } from '@react-navigation/native';
import { useEffect, useRef } from 'react';
import { PatientStackParamList } from '@/navigations/PatientStack';
import { logError } from '@/utils/logHandling';
import type { StackNavigationProp } from '@react-navigation/stack';
import { localLogoutUser } from '@/api/services/user';
import { getAccessToken, serverLogoutUser } from '@/stores/auth/services';
import { usePatientDemo } from '@/contexts/PatientDemoContext';
import { useDialog } from '@/hooks/useDialog';
import type { DialogId } from '@/components/ui/shared/Dialog/types';
import { IS_WEB } from '@/utils/platform';

export function DemoModal() {
  const {
    showDemoPopUp,
    setShowDemoPopUp,
    logoutFromDemo,
    popupDelayMs,
    popupTimerRef,
    setLogoutFromDemo,
  } = usePatientDemo();
  const { navigate } =
    useNavigation<StackNavigationProp<PatientStackParamList>>();
  const { showDialog, removeDialog } = useDialog();
  const dialogIdRef = useRef<DialogId | null>(null);

  const demoWebLogout = () => {
    if (IS_WEB) {
      const url = new URL(window.location.href);
      url.searchParams.delete('demoStarten');
      window.history.replaceState(null, '', url.toString());
    }
  };

  const confirmAction = async () => {
    demoWebLogout();
    clearTimeout(popupTimerRef.current!);
    setLogoutFromDemo(false);
    setShowDemoPopUp(false);
    await serverLogoutUser({ jwt: (await getAccessToken()) as string });
    await localLogoutUser();
    navigate('SignUpController');
  };

  const cancelAction = async () => {
    if (logoutFromDemo) {
      try {
        demoWebLogout();
        clearTimeout(popupTimerRef.current!);
        setLogoutFromDemo(false);
        setShowDemoPopUp(false);
        await serverLogoutUser({ jwt: (await getAccessToken()) as string });
        await localLogoutUser();
      } catch (err) {
        logError('Error with patient demo:', err);
        await localLogoutUser();
      }
    } else {
      setShowDemoPopUp(false);
      setTimeout(() => {
        setShowDemoPopUp(true);
      }, popupDelayMs);
    }
  };

  /*
   * The popup's visibility is owned by showDemoPopUp (timer + re-show-after-delay
   * logic lives in the actions). Mirror that boolean onto the shared Dialog stack
   * so the existing trigger flow stays untouched.
   */
  useEffect(() => {
    if (showDemoPopUp) {
      if (dialogIdRef.current !== null) {
        return;
      }
      dialogIdRef.current = showDialog({
        title: 'Danke für das Ausprobieren der Demo!',
        content:
          'Gefällt Ihnen neolexon? Dann testen Sie unsere App 14 Tage kostenlos mit individuell für Sie eingestellten Wörtern, Sätzen und Texten!',
        confirmAction: {
          label: 'Jetzt zur Testphase anmelden',
          testID: 'demo-modal-register',
          onPress: (_e, onClose) => {
            onClose?.();
            confirmAction();
          },
        },
        cancelAction: {
          label: logoutFromDemo ? 'Demo beenden' : 'weiter testen',
          testID: 'demo-modal-end',
          onPress: (_e, onClose) => {
            onClose?.();
            cancelAction();
          },
        },
        showCancelButton: true,
      });
    } else if (dialogIdRef.current !== null) {
      removeDialog(dialogIdRef.current);
      dialogIdRef.current = null;
    }
  }, [showDemoPopUp, logoutFromDemo]);

  useEffect(() => {
    return () => {
      if (dialogIdRef.current !== null) {
        removeDialog(dialogIdRef.current);
        dialogIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
